import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiX, FiMail, FiShield, FiZap, FiTrendingUp } from 'react-icons/fi'
import { useLoginPopup } from '../context/LoginPopupContext'

const LoginPopup = () => {
    const {
        loginPopup,
        closeLoginPopup,
        loginData,
        setLoginData,
        loginView,
        setLoginView,
        isLoading,
        handleLogin,
        handleForgotPassword
    } = useLoginPopup()

    return (
        <AnimatePresence>
            {loginPopup && (
                <motion.div
                    className="fixed inset-0 z-[2000] bg-[#0B0D10]/80 backdrop-blur-md flex items-center justify-center p-4"
                    onClick={closeLoginPopup}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <motion.div
                        className="relative w-full max-w-5xl bg-[#0B0D10] border border-white/10 rounded-[2.5rem] overflow-hidden flex flex-col md:flex-row shadow-2xl shadow-black/50"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ scale: 0.9, opacity: 0, y: 30 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 30 }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                    >
                        <button
                            className="absolute top-6 right-6 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/50 hover:bg-[#C21F2A] hover:text-white transition-all duration-300"
                            onClick={closeLoginPopup}
                        >
                            <FiX size={20} />
                        </button>

                        {/* Left Panel - Branding */}
                        <div className="w-full md:w-5/12 relative bg-[#14171B] overflow-hidden p-10 flex flex-col justify-between group">
                            <div className="absolute inset-0 bg-gradient-to-b from-[#1C2025] to-[#0B0D10] z-0"></div>
                            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#C21F2A]/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 z-0"></div>
                            <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#C21F2A]/5 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/3 z-0"></div>
                            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 z-0 mix-blend-overlay"></div>

                            <div className="relative z-10">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C21F2A]/10 border border-[#C21F2A]/20 backdrop-blur-sm mb-8">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#C21F2A] animate-pulse"></span>
                                    <span className="text-[10px] font-bold tracking-[0.2em] text-[#C21F2A] uppercase">System Online</span>
                                </div>

                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <h2 className="text-3xl md:text-4xl font-light text-white mb-4 tracking-tighter leading-tight">
                                        Panel <br />
                                        <span className="font-serif italic text-[#C21F2A]">Klienta</span>
                                    </h2>
                                    <p className="text-sm text-white/50 leading-relaxed font-light">
                                        Zarządzaj swoimi projektami, sprawdzaj postępy i weryfikuj dokumentację w jednym bezpiecznym miejscu.
                                    </p>
                                </motion.div>
                            </div>

                            <div className="relative z-10 space-y-6 mt-12">
                                {[
                                    { icon: FiShield, text: "Bezpieczne szyfrowanie" },
                                    { icon: FiZap, text: "Status live projektu" },
                                    { icon: FiTrendingUp, text: "Raporty i analityka" }
                                ].map((feature, idx) => (
                                    <motion.div
                                        key={idx}
                                        className="flex items-center gap-4 group/item"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 + idx * 0.1 }}
                                        whileHover={{ x: 5 }}
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center text-[#C21F2A] group-hover/item:bg-[#C21F2A] group-hover/item:text-white transition-all duration-300">
                                            <feature.icon size={18} />
                                        </div>
                                        <span className="text-sm font-medium text-white/70 group-hover/item:text-white transition-colors">
                                            {feature.text}
                                        </span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Right Panel - Form */}
                        <div className="w-full md:w-7/12 bg-[#0B0D10] p-10 flex flex-col justify-center relative">
                            <AnimatePresence mode="wait">
                                {loginView === 'login' ? (
                                    <motion.div
                                        key="login"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="w-full max-w-sm mx-auto"
                                    >
                                        <div className="mb-8">
                                            <h3 className="text-2xl font-light text-white tracking-tight mb-2">Witaj ponownie</h3>
                                            <p className="text-white/40 text-sm">Zaloguj się, aby kontynuować</p>
                                        </div>

                                        <form onSubmit={handleLogin} className="space-y-6">
                                            <div className="group">
                                                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 group-focus-within:text-[#C21F2A] transition-colors">
                                                    Adres Email
                                                </label>
                                                <div className="relative">
                                                    <FiMail className="absolute left-0 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#C21F2A] transition-colors" size={20} />
                                                    <input
                                                        type="email"
                                                        placeholder="twoj@email.pl"
                                                        value={loginData.email}
                                                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                                        className="w-full bg-transparent border-b border-white/10 py-3 pl-8 text-white placeholder:text-white/20 focus:outline-none focus:border-[#C21F2A] transition-colors"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="group">
                                                <div className="flex justify-between items-center mb-2">
                                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 group-focus-within:text-[#C21F2A] transition-colors">
                                                        Hasło
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => setLoginView('forgot')}
                                                        className="text-[10px] uppercase tracking-[0.2em] text-[#C21F2A] hover:text-white transition-colors"
                                                    >
                                                        Zapomniałeś?
                                                    </button>
                                                </div>
                                                <div className="relative">
                                                    <input
                                                        type="password"
                                                        placeholder="••••••••"
                                                        value={loginData.password}
                                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                                        className="w-full bg-transparent border-b border-white/10 py-3 text-white placeholder:text-white/20 focus:outline-none focus:border-[#C21F2A] transition-colors"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="w-full bg-[#C21F2A] text-white py-4 rounded-xl font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#A01821] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-8"
                                            >
                                                {isLoading ? 'Logowanie...' : 'Zaloguj się'}
                                            </button>
                                        </form>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="forgot"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="w-full max-w-sm mx-auto"
                                    >
                                        <div className="mb-8">
                                            <button
                                                onClick={() => setLoginView('login')}
                                                className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white mb-6 transition-colors"
                                            >
                                                ← Wróć do logowania
                                            </button>
                                            <h3 className="text-2xl font-light text-white tracking-tight mb-2">Reset Hasła</h3>
                                            <p className="text-white/40 text-sm">Wpisz swój email, wyślemy instrukcję.</p>
                                        </div>

                                        <form onSubmit={handleForgotPassword} className="space-y-6">
                                            <div className="group">
                                                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 group-focus-within:text-[#C21F2A] transition-colors">
                                                    Adres Email
                                                </label>
                                                <div className="relative">
                                                    <FiMail className="absolute left-0 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#C21F2A] transition-colors" size={20} />
                                                    <input
                                                        type="email"
                                                        placeholder="twoj@email.pl"
                                                        value={loginData.email}
                                                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                                        className="w-full bg-transparent border-b border-white/10 py-3 pl-8 text-white placeholder:text-white/20 focus:outline-none focus:border-[#C21F2A] transition-colors"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="w-full bg-[#C21F2A] text-white py-4 rounded-xl font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#A01821] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-8"
                                            >
                                                {isLoading ? 'Wysyłanie...' : 'Resetuj Hasło'}
                                            </button>
                                        </form>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default LoginPopup
